import {
  AfterContentInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChildren,
  ElementRef,
  EventEmitter,
  Inject,
  InjectionToken,
  Input,
  Output,
  QueryList,
  TemplateRef,
  ViewChild,
  HostListener,
  ViewEncapsulation,
} from '@angular/core';
import { Observable, fromEvent } from 'rxjs';
import { map, startWith, takeUntil } from 'rxjs/operators';


import {ActiveDescendantKeyManager} from '@angular/cdk/a11y';
import {coerceBooleanProperty} from '@angular/cdk/coercion';

import { AutocompleteOptionComponent } from './autocomplete-option';

/** Event object that is emitted when an autocomplete option is selected. */
export class AutocompleteSelectedEvent {
  constructor(
    /** Reference to the autocomplete panel that emitted the event. */
    public source: AutocompleteComponent,
    /** Option that was selected. */
    public option: AutocompleteOptionComponent) { }
}

/** Default `autocomplete` options that can be overridden. */
export interface AutocompleteDefaultOptions {
  /** Whether the first option should be highlighted when an autocomplete panel is opened. */
  autoActiveFirstOption?: boolean;
}

@Component({
  selector: 'app-autocomplete',
  templateUrl: 'autocomplete.html',
  styleUrls: ['autocomplete.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  exportAs: 'appAutocomplete',

})
export class AutocompleteComponent implements AfterContentInit {
  /** @docs-private */
  @ViewChild(TemplateRef) template: TemplateRef<any>;

  /** Element for the panel containing the autocomplete options. */
  @ViewChild('panel') panel: ElementRef;

  /** @docs-private */
  @ContentChildren(AutocompleteOptionComponent, { descendants: true }) options: QueryList<AutocompleteOptionComponent>;

  /** Manages active item in option list based on key events. */
  _keyManager: ActiveDescendantKeyManager<AutocompleteOptionComponent>;

  /** Function that maps an option's control value to its display value in the trigger. */
  @Input() displayWith: ((value: any) => string) | null = null;

    /**
   * Whether the first option should be highlighted when the autocomplete panel is opened.
   * Can be configured globally through the `MAT_AUTOCOMPLETE_DEFAULT_OPTIONS` token.
   */
  @Input()
  get autoActiveFirstOption(): boolean { return this._autoActiveFirstOption; }
  set autoActiveFirstOption(value: boolean) {
    this._autoActiveFirstOption = coerceBooleanProperty(value);
  }
  private _autoActiveFirstOption: boolean;

  /**
   * Specify the width of the autocomplete panel.  Can be any CSS sizing value, otherwise it will
   * match the width of its host.
   */
  @Input() panelWidth: string | number;

  /** Event that is emitted whenever an option from the list is selected. */
  @Output() readonly optionSelected: EventEmitter<AutocompleteSelectedEvent> =
      new EventEmitter<AutocompleteSelectedEvent>();


  /** Event that is emitted when the autocomplete panel is closed. */
  @Output() readonly closed: EventEmitter<void> = new EventEmitter<void>();

  /** Event that is emitted when the autocomplete panel is opened. */
  @Output() readonly opened: EventEmitter<void> = new EventEmitter<void>();

  /** Event that is emitted when the autocomplete panel is scrolled to the end. */
  @Output() readonly scollEnd: EventEmitter<void> = new EventEmitter<void>();

  /** Whether the autocomplete panel is open. */
  get isOpen(): boolean { return this._isOpen && this.showPanel; }
  _isOpen: boolean;

  /** Whether the autocomplete panel should be visible, depending on option length. */
  showPanel: boolean;

  /**
   * Takes classes set on the host mat-autocomplete element and applies them to the panel
   * inside the overlay container to allow for easy styling.
   */
  @Input('class')
  set classList(value: string) {
    if (value && value.length) {
      value.split(' ').forEach(className => this._classList[className.trim()] = true);
      this._elementRef.nativeElement.className = '';
    }
  }
  _classList: { [key: string]: boolean } = {};

  constructor(
    private _changeDetectorRef: ChangeDetectorRef,
    private _elementRef: ElementRef<HTMLElement>,
  ) {

  }

  ngAfterContentInit() {
    this._keyManager = new ActiveDescendantKeyManager<AutocompleteOptionComponent>(this.options).withWrap();
    this._setVisibility();
  }

    /**
   * Sets the panel scrollTop. This allows us to manually scroll to display options
   * above or below the fold, as they are not actually being focused when active.
   */
  _setScrollTop(scrollTop: number): void {
    if (this.panel) {
      this.panel.nativeElement.scrollTop = scrollTop;
    }
  }

  /** Returns the panel's scrollTop. */
  _getScrollTop(): number {
    return this.panel ? this.panel.nativeElement.scrollTop : 0;
  }

  /** Panel should hide itself when the option list is empty. */
  _setVisibility() {
    this.showPanel = !!this.options.length;
    this._classList['autocomplete-visible'] = this.showPanel;
    this._classList['autocomplete-hidden'] = !this.showPanel;
    this._changeDetectorRef.markForCheck();
    if (this.panel) {
      fromEvent(this.panel.nativeElement, 'scroll')
      .pipe(
        map(x => this.panel.nativeElement.scrollTop)
      )
      .subscribe(x => {
        const scrollTop = this.panel.nativeElement
          .scrollTop;
        const scrollHeight = this.panel.nativeElement
          .scrollHeight;
        const elementHeight = this.panel.nativeElement
          .clientHeight;
          const atBottom = scrollHeight === scrollTop + elementHeight;
        if (atBottom) {
          // fetch more data
          this.scollEnd.emit()
        }
      });
    }
  }

  /** Emits the `select` event. */
  _emitSelectEvent(option: AutocompleteOptionComponent): void {
    const event = new AutocompleteSelectedEvent(this, option);
    this.optionSelected.emit(event);
  }
}
